import { EVENT_CATEGORY_COLORS } from '@sirocco/shared';
import type { MapKit, PlaceMapKit } from '../events/carte/CarteApple';

/**
 * Les lieux d'une zone : ce qu'Apple Plans en connait.
 *
 * Rien n'est dans notre base. Apple rend au plus vingt lieux par question,
 * les mieux classes d'abord : une seule question pour tout perdrait donc
 * les stades sous les restaurants. On en pose une par groupe de categories.
 *
 * Les mots s'ajoutent aux categories parce qu'elles ne suffisent pas pour le
 * sport : a Poissy, la categorie « stade » ne rend rien alors qu'une recherche
 * sur « stade » trouve le stade Leo-Lagrange, classe pourtant dans cette
 * categorie. Mesure le 1er novembre 2026.
 */

export type FamilleDeLieu = 'restos' | 'sorties' | 'sport' | 'culture' | 'nature' | 'commerces';

interface Famille {
  id: FamilleDeLieu;
  couleur: string;
  /** Chaque groupe est une question a Apple. */
  groupes: string[][];
  mots?: string[];
}

/* Les couleurs des categories d'evenements : ce sont celles de l'application. */
export const FAMILLES: readonly Famille[] = [
  {
    id: 'restos',
    couleur: EVENT_CATEGORY_COLORS.food,
    groupes: [['Restaurant'], ['Cafe', 'Bakery']],
  },
  {
    id: 'sorties',
    couleur: EVENT_CATEGORY_COLORS.asso,
    groupes: [
      ['Nightlife', 'Brewery', 'Winery', 'Distillery'],
      ['MovieTheater', 'Theater', 'MusicVenue', 'Bowling', 'MiniGolf', 'GoKart', 'AmusementPark'],
    ],
  },
  {
    id: 'sport',
    couleur: EVENT_CATEGORY_COLORS.sport,
    groupes: [
      ['Stadium', 'Soccer', 'Tennis', 'Basketball', 'Swimming', 'Volleyball', 'Golf', 'Skating', 'RockClimbing', 'SkatePark'],
      ['FitnessCenter'],
    ],
    mots: ['stade', 'stadium', 'gymnase', 'piscine'],
  },
  {
    id: 'culture',
    couleur: EVENT_CATEGORY_COLORS.culture,
    groupes: [['Museum', 'Landmark', 'Castle', 'Fortress', 'NationalMonument', 'Library', 'Planetarium', 'Aquarium', 'Zoo']],
  },
  {
    id: 'nature',
    couleur: EVENT_CATEGORY_COLORS.etudes,
    groupes: [['Park', 'Beach', 'Hiking', 'NationalPark', 'Marina']],
  },
  {
    id: 'commerces',
    couleur: EVENT_CATEGORY_COLORS.pro,
    groupes: [['Store', 'FoodMarket']],
  },
];

/*
  Ce qui compte le plus sur une carte, faute de mieux.

  Apple ne donne ni note ni frequentation. Le genre du lieu dit donc l'essentiel : un
  stade ou un musee se cherchent, et il y en a peu ; une boutique, il y en a cent. Le
  reste de l'importance vient de ce qu'on sait du lieu et du rang que lui donne Apple.
*/
const POIDS_DES_CATEGORIES: Record<string, number> = {
  Stadium: 10, Museum: 10, Castle: 10, Fortress: 10, NationalMonument: 10, Landmark: 9,
  AmusementPark: 10, Zoo: 10, Aquarium: 10, Planetarium: 9, NationalPark: 9, Beach: 9,
  MovieTheater: 9, Theater: 9, MusicVenue: 8, Park: 7, Hiking: 6, Marina: 6,
  Swimming: 8, Golf: 7, Skating: 8, RockClimbing: 7, Soccer: 7, Tennis: 7, Basketball: 7,
  Volleyball: 6, SkatePark: 6, Bowling: 7, MiniGolf: 6, GoKart: 7, Library: 6,
  Nightlife: 6, Brewery: 6, Winery: 6, Distillery: 6, Restaurant: 5, FoodMarket: 5,
  Cafe: 4, Bakery: 3, FitnessCenter: 2, Store: 2,
};
const POIDS_INCONNU = 3;

export interface LieuTrouve {
  id: string;
  /** Plus c'est grand, plus il a de chances de paraitre quand la place manque. */
  importance: number;
  nom: string;
  lat: number;
  lon: number;
  famille: FamilleDeLieu;
  /** La categorie d'Apple, quand il en donne une. */
  categorie: string | null;
  adresse: string;
  telephone: string | null;
  site: string | null;
}

export interface VueDeCarte {
  lat: number;
  lon: number;
  etendueLat: number;
  etendueLon: number;
}

export const normaliser = (texte: string) =>
  texte
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

const FAMILLE_DE_CATEGORIE = new Map(
  FAMILLES.flatMap((famille) => famille.groupes.flat().map((categorie) => [categorie, famille.id] as const)),
);

/** Seulement http et https : ce lien part dans un `href`, et vient d'un tiers. */
function siteDe(place: PlaceMapKit): string | null {
  const url = place.urls?.find((lien) => /^https?:\/\//i.test(lien));
  return url ?? null;
}

/**
 * @param rang La place dans la reponse d'Apple, de 0 (la premiere) a 1 (la derniere).
 * @param decoupes Combien de fois la zone a ete coupee : etre premier d'une petite zone
 * dit moins qu'etre premier de toute la vue.
 */
function enLieu(place: PlaceMapKit, famille: FamilleDeLieu, rang: number, decoupes: number): LieuTrouve {
  const categorie = place.pointOfInterestCategory;
  return {
    id: place.id ?? `${place.name}|${place.coordinate.latitude}|${place.coordinate.longitude}`,
    importance:
      ((categorie && POIDS_DES_CATEGORIES[categorie]) || POIDS_INCONNU) +
      (siteDe(place) ? 1.5 : 0) +
      (place.telephone ? 0.5 : 0) +
      (2 * (1 - rang)) / (1 + decoupes),
    nom: place.name,
    lat: place.coordinate.latitude,
    lon: place.coordinate.longitude,
    famille,
    categorie: place.pointOfInterestCategory ?? null,
    adresse: place.formattedAddress ?? '',
    telephone: place.telephone ?? null,
    site: siteDe(place),
  };
}

/*
  Une recherche par mot ramene de tout : « stade » rend aussi l'arret de bus
  « Rue du Stade », et « piscine » un artisan « specialiste piscine ». On ne
  garde que ce qui est classe dans la famille, ou ce qui n'est pas classe mais
  dont le nom commence par le mot.
*/
function correspond(place: PlaceMapKit, famille: Famille, mot: string): boolean {
  const categorie = place.pointOfInterestCategory;
  if (categorie) return FAMILLE_DE_CATEGORIE.get(categorie) === famille.id;
  return normaliser(place.name).startsWith(normaliser(mot));
}

/* Le meme lieu peut sortir de deux questions, voire sous deux identifiants. */
const memoire = new Map<string, LieuTrouve>();
const signatures = new Set<string>();
/* Ce qu'on a deja demande : on ne repaie pas une question pour un petit geste. */
const dejaVu: { famille: FamilleDeLieu; vue: VueDeCarte }[] = [];

/** Ce qu'on a trouve depuis le lancement : revenir sur l'onglet le retrouve. */
export function lieuxConnus(): LieuTrouve[] {
  return [...memoire.values()];
}

function retenir(lieu: LieuTrouve): boolean {
  const signature = `${normaliser(lieu.nom)}|${lieu.lat.toFixed(3)}|${lieu.lon.toFixed(3)}`;
  if (memoire.has(lieu.id) || signatures.has(signature)) return false;
  memoire.set(lieu.id, lieu);
  signatures.add(signature);
  return true;
}

/*
  Une vue deja couverte ne se redemande pas, sauf si l'on s'est bien rapproche.
*/
function couverte(famille: FamilleDeLieu, vue: VueDeCarte): boolean {
  return dejaVu.some(
    (passe) =>
      passe.famille === famille &&
      vue.etendueLat >= passe.vue.etendueLat / 2 &&
      dedans(vue, passe.vue),
  );
}

function dedans(petite: VueDeCarte, grande: VueDeCarte): boolean {
  return (
    Math.abs(petite.lat - grande.lat) + petite.etendueLat / 2 <= grande.etendueLat / 2 &&
    Math.abs(petite.lon - grande.lon) + petite.etendueLon / 2 <= grande.etendueLon / 2
  );
}

/*
  Une zone qui repond moins que le maximum a tout dit.

  Apple rend au plus vingt lieux, les plus centraux d'abord : sur une vue large, tout
  se concentrait au milieu et les bords restaient vides. Une zone qui sature est donc
  coupee en quatre, et chaque quart redemande. Les zones denses se decoupent, les
  zones calmes ne coutent qu'une question, et l'on retient ce qui est complet pour ne
  pas le redemander en se rapprochant.
*/
const MAX_PAR_REPONSE = 20;
const DECOUPES_MAX = 2;
/* Le quota d'Apple est partage par tous les membres : une vue n'en prend pas plus. */
const QUESTIONS_PAR_VUE = 40;
const complets = new Map<string, VueDeCarte[]>();

function quarts(zone: VueDeCarte): VueDeCarte[] {
  const etendueLat = zone.etendueLat / 2;
  const etendueLon = zone.etendueLon / 2;
  return [-1, 1].flatMap((nordSud) =>
    [-1, 1].map((estOuest) => ({
      lat: zone.lat + (nordSud * etendueLat) / 2,
      lon: zone.lon + (estOuest * etendueLon) / 2,
      etendueLat,
      etendueLon,
    })),
  );
}

interface Question {
  famille: Famille;
  groupe: string[];
  zone: VueDeCarte;
  decoupes: number;
}

/**
 * Demande a Apple les lieux de la vue, pour les familles donnees.
 *
 * `onTrouves` est appele chaque fois que des lieux nouveaux sont connus : les zones
 * decoupees arrivent apres les autres, et la carte se remplit au fur et a mesure.
 * Une question qui echoue ne fait rien tomber : le reste de la carte vaut mieux que
 * rien, et le prochain geste reposera la question.
 */
export async function chercherLesLieux(
  mapkit: MapKit,
  vue: VueDeCarte,
  familles: readonly FamilleDeLieu[],
  langue: string,
  onTrouves: () => void,
): Promise<void> {
  const regionDe = (zone: VueDeCarte) =>
    new mapkit.CoordinateRegion(
      new mapkit.Coordinate(zone.lat, zone.lon),
      new mapkit.CoordinateSpan(zone.etendueLat, zone.etendueLon),
    );
  const options = { region: regionDe(vue), language: langue };
  let restantes = QUESTIONS_PAR_VUE;
  let file: Question[] = [];
  const mots: Promise<number>[] = [];

  for (const famille of FAMILLES) {
    if (!familles.includes(famille.id) || couverte(famille.id, vue)) continue;
    dejaVu.push({ famille: famille.id, vue });
    file.push(...famille.groupes.map((groupe) => ({ famille, groupe, zone: vue, decoupes: 0 })));
    for (const mot of famille.mots ?? []) {
      restantes -= 1;
      mots.push(
        new Promise((rendre) => {
          new mapkit.Search({
            ...options,
            includeAddresses: false,
            includePhysicalFeatures: false,
            includeQueries: false,
            regionPriority: mapkit.Search.RegionPriority.Required,
          }).search(mot, (erreur, reponse) => {
            if (erreur) return rendre(0);
            rendre(
              reponse.places
                .filter((place) => correspond(place, famille, mot))
                .filter((place, rang, tous) => retenir(enLieu(place, famille.id, rang / tous.length, 0))).length,
            );
          });
        }),
      );
    }
  }

  const poser = ({ famille, groupe, zone, decoupes }: Question): Promise<Question[]> =>
    new Promise((rendre) => {
      const filtre = mapkit.PointOfInterestFilter.including(
        groupe.map((categorie) => mapkit.PointOfInterestCategory[categorie]!),
      );
      new mapkit.PointsOfInterestSearch({
        region: regionDe(zone),
        language: langue,
        pointOfInterestFilter: filtre,
      }).search((erreur, reponse) => {
        if (erreur) return rendre([]);
        reponse.places.forEach((place, rang, tous) =>
          retenir(enLieu(place, famille.id, rang / tous.length, decoupes)),
        );
        if (reponse.places.length < MAX_PAR_REPONSE) {
          const cle = groupe.join();
          complets.set(cle, [...(complets.get(cle) ?? []), zone]);
          return rendre([]);
        }
        rendre(
          decoupes < DECOUPES_MAX
            ? quarts(zone).map((quart) => ({ famille, groupe, zone: quart, decoupes: decoupes + 1 }))
            : [],
        );
      });
    });

  /* Largeur d'abord : si le quota de la vue s'epuise, c'est partout a la fois. */
  const motsFinis = Promise.all(mots).then((nouveaux) => {
    if (nouveaux.some((nombre) => nombre > 0)) onTrouves();
  });
  while (file.length > 0 && restantes > 0) {
    const utiles = file.filter(
      (question) => !complets.get(question.groupe.join())?.some((zone) => dedans(question.zone, zone)),
    );
    const lot = utiles.slice(0, restantes);
    restantes -= lot.length;
    const avant = memoire.size;
    file = (await Promise.all(lot.map(poser))).flat();
    if (memoire.size > avant) onTrouves();
  }
  await motsFinis;
}

/*
  Ce qu'une recherche ramene et qui n'est pas une activite : « stade » rend l'arret de bus
  « Rue du Stade ». Le reste passe, meme hors de nos familles : qui cherche un coiffeur
  veut un coiffeur, et il prend alors la pastille des commerces.
*/
const HORS_SUJET = new Set(['PublicTransport', 'Parking', 'EVCharger']);
/** Un lieu trouve dont on ne sait pas le genre : ni une boutique, ni rien d'autre. */
export const SANS_GENRE = 'SansGenre';
const RESULTATS = new Map<string, LieuTrouve[]>();

/**
 * Ce qu'on cherche dans la barre, partout dans la zone.
 *
 * Le mot, et la categorie quand c'est exactement le nom d'un type : l'une trouve ce que
 * l'autre ne trouve pas (voir plus haut). Une reponse saturee se coupe une fois en quatre,
 * pour que les resultats couvrent la zone et pas seulement son milieu. Ils ne rejoignent
 * pas ce que la carte montre d'habitude, et une recherche deja faite ne se repaie pas.
 */
export async function chercherDansLaZone(
  mapkit: MapKit,
  zone: VueDeCarte,
  texte: string,
  categorie: string | null,
  langue: string,
): Promise<LieuTrouve[]> {
  const cle = `${normaliser(texte)}|${categorie ?? ''}|${zone.lat}|${zone.lon}`;
  const connus = RESULTATS.get(cle);
  if (connus) return connus;

  const regionDe = (ici: VueDeCarte) =>
    new mapkit.CoordinateRegion(
      new mapkit.Coordinate(ici.lat, ici.lon),
      new mapkit.CoordinateSpan(ici.etendueLat, ici.etendueLon),
    );
  const parMot = (ici: VueDeCarte) =>
    new Promise<PlaceMapKit[]>((rendre) => {
      new mapkit.Search({
        region: regionDe(ici),
        language: langue,
        includeAddresses: false,
        includePhysicalFeatures: false,
        includeQueries: false,
        regionPriority: mapkit.Search.RegionPriority.Required,
      }).search(texte, (erreur, reponse) => rendre(erreur ? [] : reponse.places));
    });
  const parCategorie = (ici: VueDeCarte) =>
    new Promise<PlaceMapKit[]>((rendre) => {
      new mapkit.PointsOfInterestSearch({
        region: regionDe(ici),
        language: langue,
        pointOfInterestFilter: mapkit.PointOfInterestFilter.including([mapkit.PointOfInterestCategory[categorie!]!]),
      }).search((erreur, reponse) => rendre(erreur ? [] : reponse.places));
    });
  const partout = async (question: (ici: VueDeCarte) => Promise<PlaceMapKit[]>) => {
    const places = await question(zone);
    if (places.length < MAX_PAR_REPONSE) return places;
    return [...places, ...(await Promise.all(quarts(zone).map(question))).flat()];
  };

  const places = (
    await Promise.all([partout(parMot), ...(categorie && mapkit.PointOfInterestCategory[categorie] ? [partout(parCategorie)] : [])])
  ).flat();

  const mot = normaliser(texte);
  const gardes = new Map<string, LieuTrouve>();
  const signes = new Set<string>();
  places.forEach((place, rang) => {
    const genre = place.pointOfInterestCategory;
    if (genre && HORS_SUJET.has(genre)) return;
    const nom = normaliser(place.name);
    const famille = genre ? FAMILLE_DE_CATEGORIE.get(genre) : undefined;
    const lieu = enLieu(place, famille ?? 'commerces', rang / places.length, 0);
    if (categorie) {
      /*
        On a demande un type. Apple laisse sans categorie des stades municipaux, et range le
        Stade de France dans les monuments : ce qui en porte le nom est du type demande. Sauf
        les boutiques : « Piscine » ne doit pas rendre les magasins de piscines.
      */
      const voulue = FAMILLE_DE_CATEGORIE.get(categorie);
      if (famille !== voulue) {
        if (!nom.includes(mot) || (genre && (!famille || famille === 'commerces'))) return;
        if (voulue) lieu.famille = voulue;
        lieu.categorie = categorie;
      }
    } else if (!famille) {
      /* Sans categorie, seul son nom dit qu'il repond. Et le logo d'une boutique serait faux. */
      if (!genre && !nom.includes(mot)) return;
      lieu.categorie = SANS_GENRE;
    }
    const signe = `${normaliser(lieu.nom)}|${lieu.lat.toFixed(3)}|${lieu.lon.toFixed(3)}`;
    if (gardes.has(lieu.id) || signes.has(signe)) return;
    gardes.set(lieu.id, lieu);
    signes.add(signe);
  });
  const trouves = [...gardes.values()];
  /* Une panne ne se retient pas : la prochaine fois, on redemande. */
  if (trouves.length > 0) RESULTATS.set(cle, trouves);
  return trouves;
}
